const RESIZE_OBSERVER_MESSAGES = [
  'ResizeObserver loop completed with undelivered notifications.',
  'ResizeObserver loop limit exceeded'
]

const isResizeObserverNotice = (message: unknown): boolean =>
  typeof message === 'string' &&
  RESIZE_OBSERVER_MESSAGES.some((m) => message.indexOf(m) !== -1)

let installed = false

/**
 * rsuite (cards, dropdowns, modals, overlays) observes element size via the
 * `@juggle/resize-observer` polyfill. When an observed element reflows during a
 * callback the polyfill emits the benign
 * "ResizeObserver loop completed with undelivered notifications." error by
 * calling `window.dispatchEvent(new ErrorEvent('error', ...))`.
 *
 * Because that event is dispatched directly on `window`, `window` is the event
 * target — so capture-vs-bubble ordering does not apply and listeners fire in
 * registration order. The CRA / webpack-dev-server error overlay registers its
 * `error` listener before our app code runs, so a normal `addEventListener`
 * with `stopImmediatePropagation` can never beat it. We therefore intercept
 * `dispatchEvent` itself and drop this one specific, harmless event before any
 * listener (including the dev overlay) can see it.
 */
const suppressResizeObserverLoopError = (): void => {
  if (installed || typeof window === 'undefined') return
  installed = true

  const originalDispatchEvent = window.dispatchEvent.bind(window)

  window.dispatchEvent = (event: Event): boolean => {
    const message = (event as ErrorEvent)?.message
    if (event?.type === 'error' && isResizeObserverNotice(message)) {
      // Report it as successfully dispatched and un-cancelled, but never
      // actually deliver it to any listener.
      return true
    }
    return originalDispatchEvent(event)
  }

  // Safety net for browsers/paths that surface the notice through the global
  // error handler instead of an explicit dispatchEvent call.
  window.addEventListener(
    'error',
    (event: ErrorEvent) => {
      if (isResizeObserverNotice(event?.message)) {
        event.stopImmediatePropagation()
        event.preventDefault()
      }
    },
    true
  )
}

export default suppressResizeObserverLoopError
